import { Injectable, signal } from '@angular/core';
import { TripstatsRecord } from '../class/TripStats';
import { LocalStorageService } from './local-storage.service';

/**
 * Holds the trip stats cache (per dashboard widget index) in isolation from any business
 * logic. Kept dependency-free on purpose - both TripStatsService and PreferenceService need
 * to read/mutate this cache, and if either of them owned it directly the other would have
 * to inject it back, creating a circular DI dependency (TripStatsService already injects
 * PreferenceService). A standalone store with no dependencies of its own can safely be
 * injected from both sides.
 */
@Injectable({
  providedIn: 'root',
})
export class TripstatsStoreService {
  /**
   * Consumers mutate the per-widget entries in place (e.g.
   * `tripstats[index].tripstats.data_compare = ...`) rather than rebuilding the whole
   * record, so `equal: () => false` is required - a plain `.set()` of the same mutated
   * reference would otherwise be considered "no change" by the default Object.is check
   * and consumers wouldn't be notified. Always mutate through `mutate`/`deleteAt` (or
   * `.set()` with a fresh value) instead of writing to `this.$tripstats()` directly.
   */
  public $tripstats = signal<TripstatsRecord>({}, { equal: () => false });

  public initFromStorage(): void {
    this.$tripstats.set(LocalStorageService.getElement('tripstats')?.stats || {});
  }

  public mutate(mutator: (tripstats: TripstatsRecord) => void): void {
    const tripstats = this.$tripstats();
    mutator(tripstats);
    this.$tripstats.set(tripstats);
  }

  public deleteAt(index: number): void {
    this.mutate(tripstats => {
      delete tripstats[index];
    });
  }
}
